<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { useContactStore } from '../Stores/contact'
import SubmitButton from './SubmitButton.vue'

const contactStore = useContactStore()
const { isDirty, hasError, message } = storeToRefs(contactStore)
</script>

<template>
    <TransitionRoot as="template" :show="contactStore.isOpen">
        <Dialog @close="contactStore.close()" as="div" class="relative z-10">
            <TransitionChild
                as="template"
                enter="ease-in-out duration-500"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-500"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div
                    class="fixed inset-0 bg-gray-500/75 transition-opacity lg:mx-auto lg:max-w-xl"
                />
            </TransitionChild>
            <div class="fixed inset-0 overflow-hidden lg:mx-auto lg:max-w-xl">
                <div class="absolute inset-0 overflow-hidden">
                    <div class="pointer-events-none fixed bottom-0 flex max-w-full">
                        <TransitionChild
                            as="template"
                            enter="transform transition ease-in-out duration-500 sm:duration-700"
                            enter-from="translate-y-full"
                            enter-to="translate-y-0"
                            leave="transform transition ease-in-out duration-500 sm:duration-700"
                            leave-from="translate-y-0"
                            leave-to="translate-y-full"
                        >
                            <DialogPanel
                                class="pointer-events-auto w-screen lg:mx-auto lg:max-w-xl"
                            >
                                <div
                                    class="flex h-full flex-col overflow-y-scroll rounded-t-2xl bg-white p-4 shadow-xl"
                                >
                                    <div class="flex items-start justify-between">
                                        <DialogTitle
                                            class="text-lg font-semibold leading-6 text-neutral-900"
                                        >
                                            {{ $t('Contact Support') }}
                                        </DialogTitle>
                                        <div class="ml-3 flex h-7 items-center">
                                            <button
                                                @click="contactStore.close()"
                                                type="button"
                                                class="rounded-md bg-white text-blue-700 hover:text-primary-800 focus:outline-hidden focus:ring-2 focus:ring-blue-700 focus:ring-offset-2"
                                            >
                                                <span class="sr-only">Close</span>
                                                <XMarkIcon class="size-5" aria-hidden="true" />
                                            </button>
                                        </div>
                                    </div>
                                    <div class="relative mt-4 flex-1">
                                        <div class="space-y-4">
                                            <p class="text-sm text-neutral-700">
                                                {{
                                                    $t(
                                                        'Need urgent help, or want to file a complaint about your contractor? We’re here to help. Expect an answer within 2 business days.'
                                                    )
                                                }}
                                            </p>
                                            <hr />
                                            <label
                                                for="message"
                                                class="block text-sm font-medium text-neutral-900"
                                            >
                                                {{ $t('Describe your issue:') }}
                                            </label>
                                            <textarea
                                                v-model.trim="message"
                                                @input.once="isDirty = true"
                                                rows="6"
                                                id="message"
                                                class="block w-full resize-none rounded-md border-0 py-2 text-sm placeholder:text-neutral-400 text-neutral-700 shadow-xs ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-blue-600"
                                                :class="
                                                    isDirty && hasError
                                                        ? 'text-danger-600 ring-danger-300 placeholder:text-danger-300 focus:ring-danger-500'
                                                        : 'text-neutral-700 ring-neutral-300 placeholder:text-neutral-400 focus:ring-blue-600'
                                                "
                                            />
                                        </div>
                                    </div>
                                    <div class="mt-6 mb-4">
                                        <SubmitButton @click="contactStore.submit">
                                            {{ $t('Send') }}
                                        </SubmitButton>
                                    </div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
